/**
 * 퀘스트 쿼리 키 팩토리.
 *
 * 키는 **생성처와 무효화처의 레이어가 다르다** — 조회하는 쪽은 `pages/*`·`entities/quest`,
 * 무효화하는 쪽은 `features/*` 의 mutation 이다. 게다가 형제 엔티티(`entities/quest-task` 의
 * 팀 초록 잔여 조회, `entities/quest-template` 의 템플릿 mutation)도 같은 키를 참조한다.
 * FSD 는 features → pages 도, entity → 형제 entity 도 막으므로 **모든 레이어가 합법적으로
 * 참조 가능한 shared 에 키를 둔다.** (리터럴을 양쪽에 복붙하면 한쪽만 바뀌어도 타입 에러 없이
 * 조용히 stale 된다.)
 *
 * `entities/quest` 배럴이 이 팩토리를 그대로 재수출하므로 기존 소비처(`@/entities/quest`)는
 * 그대로 둔다 — shared 를 직접 참조해야 하는 건 형제 엔티티뿐이다.
 *
 * 모든 키는 **`['quests']` 접두사 안**에 둔다 — 접두사 이탈은 곧 회귀이므로
 * `__tests__/questKeys.test.ts` 가 팩토리 자체를 단언으로 못박는다.
 */

import { defineQueryKeys } from '@/shared/lib/queryKeys';

/** `useQuestList` 가 받는 목록 필터. 키의 일부이므로 조합이 바뀌면 캐시 엔트리도 갈린다. */
export interface QuestListFilters {
  scope?: 'TEAM' | 'GLOBAL';
  teamId?: string;
  status?: string;
  approvalStatus?: string;
  limit?: number;
  archive?: 'exclude' | 'only';
  /**
   * 업무명 검색어(퀘스트 69aede34). 서버가 퀘스트 제목·설명과 태스크 제목을 본다 —
   * 태스크 제목이 목록 행에 안 실려 화면에서는 거를 수 없기 때문이다.
   * `questKeys.list` 에 실리므로 검색어마다 캐시가 갈린다.
   */
  search?: string;
}

/**
 * 퀘스트 도메인 루트(`questKeys.all`) — **광역 무효화 지점이 쓰는 키**다.
 *
 * 이 접두사는 걷어낼 대상이 아니라 **설계된 커버 메커니즘**이다. 아래 배치 요약 키들이
 * 접두사 안에 있다는 사실만으로 자동 갱신되는 근거가 바로 이 광역 무효화다.
 * 좁히면 그 커버가 깨진다 — `pendingReviewSummaries` 도크스트링의 "두 번 부도" 참조.
 *
 * 리터럴 `['quests']` 를 각 mutation 에 복붙하는 대신 `questKeys.all` 을 쓴다. 조회와 무효화가
 * 같은 함수를 참조해야 한 쪽만 어긋나는 조용한 stale 을 원천 차단할 수 있다 —
 * `queryKey` 가 `readonly unknown[]` 이라 오타는 타입으로 잡히지 않는다.
 *
 * `all` 은 `defineQueryKeys` 가 루트에서 만들어 아래 콜백에 넘긴다. 멤버가 그걸 스프레드하므로
 * `'quests'` 리터럴은 이 파일에 **한 번만** 등장하고, 접두사 밖으로 나가는 키는 컴파일이 거부한다.
 * 회귀 가드: `__tests__/questKeys.test.ts`.
 */
export const questKeys = defineQueryKeys('quests', (all) => ({
  /**
   * ── 무효화 전용 중간 접두사 ────────────────────────────────────────────
   *
   * 아래 키들은 **조회에 쓰지 않는다.** 뮤테이션이 도메인 전체(`all`)가 아니라 특정
   * 하위 묶음만 무효화하고 싶을 때 쓰는 접두사다.
   *
   * 이게 없으면 좁히기가 **표현 자체가 불가능**했다. 예를 들어 `useRejectQuest` 는
   * `{ id, reason }` 만 받아서 화면에 어떤 `filters` 조합이 마운트돼 있는지 모른다.
   * `list()` 를 부르면 `['quests','list',undefined]` 가 나오는데 이건
   * `['quests','list',{scope:'TEAM'}]` 의 prefix 가 **아니다**(3번째 자리 불일치).
   * 목록 전체를 덮으려면 2개짜리 `['quests','list']` 가 필요하다 — 그게 `listAll` 이다.
   *
   * **추가한다고 좁혀지는 건 아니다.** 실제로 좁히려면 광역 무효화를 단언하는 기존 가드
   * (`useApproveQuest.test.tsx` · `useBulkApproveQuestsFromCeo.test.tsx` ·
   * `features/checkpoint-review/model/__tests__/pendingReviewInvalidation.test.ts`)를
   * 먼저 손봐야 하고, 그 가드들이 막으려던 회귀를 감수한다는 결정이 따로 필요하다.
   * 여기서는 **선택지를 만들어 두기만** 한다.
   *
   * 각 접두사가 짝 함수의 진짜 prefix 인지는 `__tests__/questKeys.test.ts` 가 단언한다.
   */
  contextAll: [...all, 'list-context'] as const,
  context: (filters?: Omit<QuestListFilters, 'limit' | 'archive'>) =>
    [...all, 'list-context', filters] as const,
  listAll: [...all, 'list'] as const,
  acceptedAll: [...all, 'accepted'] as const,
  challengeAll: [...all, 'challenge'] as const,
  vaRequestsAll: [...all, 'va-requests'] as const,
  adcodeRequestsAll: [...all, 'adcode-requests'] as const,

  /** 퀘스트 목록 — 필터 조합마다 별도 캐시 엔트리. 무효화는 `listAll`. */
  list: (filters?: QuestListFilters) => [...all, 'list', filters] as const,
  /**
   * 퀘스트 상세. 조회(`useQuest`)와 저장 후 재검증(`useUpdateQuest`)이 **같은 키**를 봐야 한다 —
   * 리터럴이던 시절 두 파일이 각자 `['quests', id]` 를 적고 있었다.
   */
  detail: (questId: string | undefined) => [...all, questId] as const,
  /** 퀘스트별 수락자 목록. `detail` 하위라 상세 무효화가 함께 커버한다. */
  acceptances: (questId: string | undefined) => [...all, questId, 'acceptances'] as const,
  /** 증빙 미제출 수락자 목록(퀘스트 b1cfcba4). `detail` 하위라 상세 무효화가 함께 커버한다. */
  proofReminderTargets: (questId: string | undefined) =>
    [...all, questId, 'proof-reminder-targets'] as const,
  /**
   * 내가 수락한 퀘스트. 리터럴이던 시절 `entities/quest/model/useAcceptedQuests.ts` 와
   * `features/leaderboard/model/useQuestTensions.ts` 가 **각자** 같은 배열을 적고 있었다 —
   * 한쪽만 고치면 두 화면이 서로 다른 캐시를 보게 된다.
   */
  accepted: (userId: string | undefined) => [...all, 'accepted', userId] as const,
  /** 팀 도전 현황(잔여 수량). */
  challenge: (userId: string | undefined) => [...all, 'challenge', userId] as const,
  /** 시나리오별 VA 요청. */
  vaRequests: (scenarioId: string | undefined) => [...all, 'va-requests', scenarioId] as const,
  /** 시나리오별 ADCODE 요청. */
  adcodeRequests: (scenarioId: string | undefined) =>
    [...all, 'adcode-requests', scenarioId] as const,
  /**
   * VA 하달 카드(EN 번역). **`['quests']` 접두사 안**에 두는 것이 의도 — 퀘스트가 바뀌면
   * 번역도 다시 받아야 하므로 광역 무효화가 그대로 닿아야 한다.
   *
   * 세그먼트 순서가 다른 멤버들과 반대다(`questId` 가 리터럴보다 **앞**). 리터럴이던 시절의
   * 모양을 그대로 옮긴 것이고, 바꾸면 캐시 키가 달라진다 — 게이트 승격은 모양을 안 건드린다.
   */
  dispatchCard: (questId: string) => [...all, questId, 'dispatch-card'] as const,
  /**
   * 시나리오 수락 인원 패널(팀장 뷰). `acceptanceSummaries` 와 같은 사유로 **`['quests']`
   * 접두사 안**에 둔다 — 수락·포기가 곧 이 표의 내용이라 수락 축 무효화가 그대로 닿아야 한다.
   * **여기에 무효화를 새로 추가하지 마라** — 접두사가 이미 커버한다.
   */
  scenarioAcceptances: (scenarioId: string | undefined) =>
    [...all, 'scenario-acceptances', scenarioId] as const,

  /**
   * 목록 행의 파생 배지(도전자 수·검토 대기·태스크 존재·체크포인트 집계)는 별도 키가 없다 —
   * `GET /quests` 응답에 실려 오므로 `list` 캐시가 곧 그 캐시다. 예전에는 배치 요약 셋이
   * 각자 키를 갖고 있었고, 그 키가 퀘스트 id 전량을 담아 캐시가 한 건만 늘어도 통째로
   * 미스났다(그리고 그 id 목록이 URL 을 넘겨 431 을 냈다 — 2026-08-27).
   * 배지를 바꾸는 mutation 은 `listAll`(또는 접두사인 `all`)을 무효화하면 된다.
   */
}));
